import { UtteranceModel } from "../../transcript/core/_models";
import { ThemeModeType } from "../../../../_metronic/partials";
import {
  ChapterModel,
  EventWordModel,
  TagModel,
  WordChapterModel,
} from "./_models";
import {
  getAbsoluteOffsetTop,
  hexToRGBA,
} from "../../transcript/core/_helpers";

export const calculateCountdown = (startDate: string) => {
  const targetDate = new Date(startDate);
  const now = new Date();
  const difference = targetDate.getTime() - now.getTime();

  if (difference < 0) {
    return { countdown: "Event will start soon!", startRefreshing: true };
  } else if (difference < 300000) {
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((difference / 1000 / 60) % 60);
    const seconds = Math.floor((difference / 1000) % 60);

    return {
      countdown: `${days}d ${hours}h ${minutes}m ${seconds}s`,
      startRefreshing: true,
    };
  } else {
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((difference / 1000 / 60) % 60);
    const seconds = Math.floor((difference / 1000) % 60);

    return {
      countdown: `${days}d ${hours}h ${minutes}m ${seconds}s`,
      startRefreshing: false,
    };
  }
};

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);

  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };

  const formattedDate = new Intl.DateTimeFormat("en-US", options).format(date);

  return formattedDate;
};

export const formatQuestionDate = (dateString: string) => {
  const date = new Date(dateString);

  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };

  const formattedDate = new Intl.DateTimeFormat("en-US", options).format(date);

  return formattedDate;
};

export const convertStringToMiliSeconds = (time: string) => {
  const timeArray = time.split(":");
  const hours = parseInt(timeArray[0]);
  const minutes = parseInt(timeArray[1]);
  const seconds = parseInt(timeArray[2]);

  var timeInSec = hours * 3600000 + minutes * 60000 + seconds * 1000;

  return timeInSec;
};

export const formatAnswerTime = (time: number) => {
  time = Math.floor(time / 1000);
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = time % 60;

  const formattedTime =
    hours.toString().padStart(2, "0") +
    ":" +
    minutes.toString().padStart(2, "0") +
    ":" +
    seconds.toString().padStart(2, "0");

  return formattedTime;
};

export const calculateTime = (questionDate: string): string => {
  const date = new Date(questionDate);
  const now = new Date();
  const difference = now.getTime() - date.getTime();

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  if (days > 0) return `${days} days ago`;

  const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
  if (hours > 0) return `${hours} hours ago`;

  const minutes = Math.floor((difference / 1000 / 60) % 60);
  return `${minutes} minutes ago`;
};

export const anonymizeName = (name: string) => {
  if (!name) return "";
  return name[0] + "*".repeat(4) + " " + "*".repeat(5);
};

export const applySentimentColors = (word: EventWordModel) => {
  var colors = {
    neutral: "",
    positive: "positive-sentiment",
    negative: "negative-sentiment",
  };

  if (word.sentiment == "neutral") {
    return colors.neutral;
  }
  if (word.sentiment == "positive") {
    return colors.positive;
  }
  return colors.negative;
};

export const applySentimentForPopup = (
  calculatedMode: ThemeModeType,
  sentiment: string
) => {
  if (calculatedMode == "dark") {
    var colors = {
      positive: "#1d801d",
      negative: "#a31717",
    };
  } else {
    var colors = {
      positive: "#8bfc8ba2",
      negative: "#fa7d7d96",
    };
  }
  if (sentiment == "positive") {
    return colors.positive;
  }
  return colors.negative;
};

export const groupWordsByChapter = (
  utterances: UtteranceModel[],
  words: EventWordModel[],
  chapters?: ChapterModel[]
) => {
  const newWordChapters: WordChapterModel[] = words.map((word) =>
    mapWordToChapter(word, utterances, chapters)
  );
  return groupWordsByUtterancesAndChapters(newWordChapters);
};

const mapWordToChapter = (
  word: EventWordModel,
  utterances: UtteranceModel[],
  chapters?: ChapterModel[]
): WordChapterModel => {
  let utteranceIndex = 0;

  // Check if the word's end time is larger than the current utterance's end time
  while (
    utteranceIndex < utterances.length &&
    word.end > utterances[utteranceIndex].end
  ) {
    utteranceIndex++;
  }

  const currentUtterance = utterances[utteranceIndex];
  if(chapters){
    for (let chapter of chapters) {
      if (word.start >= chapter.start && word.start <= chapter.end) {
        return { word, chapter, utterance: currentUtterance };
      }
    }
  }
  
  return { word, utterance: currentUtterance };
};

const groupWordsByUtterancesAndChapters = (
  newWordChapters: WordChapterModel[]
): WordChapterModel[][] => {
  const splitWordChapters: WordChapterModel[][] = [];
  let lastChapterStart: number | null = null;
  let lastUtteranceEnd: number | null = null;

  newWordChapters.forEach((item) => {
    if (item.chapter) {
      if (
        lastChapterStart === item.chapter.start &&
        (lastUtteranceEnd === null || item.word.end <= lastUtteranceEnd)
      ) {
        splitWordChapters[splitWordChapters.length - 1].push(item);
      } else {
        splitWordChapters.push([item]);
        lastChapterStart = item.chapter.start;
      }
    } else {
      if (
        lastChapterStart !== null ||
        splitWordChapters.length === 0 ||
        (lastUtteranceEnd !== null && item.word.end > lastUtteranceEnd)
      ) {
        splitWordChapters.push([item]);
        lastChapterStart = null;
      } else {
        splitWordChapters[splitWordChapters.length - 1].push(item);
      }
    }
    lastUtteranceEnd = item.utterance!.end;
  });

  return splitWordChapters;
};

export const updateHeaderTitle = (isIntersecting: boolean) => {
  const eventTitle = document.getElementById("header-event-title");
  if (isIntersecting && eventTitle) {
    eventTitle.classList.remove("visible");
  }
  if (!isIntersecting && eventTitle) {
    eventTitle.classList.add("visible");
  }
};

const languageDictionary: { [key: string]: string } = {
  AR: "Arabic",
  BG: "Bulgarian",
  CS: "Czech",
  DA: "Danish",
  DE: "German",
  EL: "Greek",
  EN: "English",
  "EN-GB": "English",
  ES: "Spanish",
  ET: "Estonian",
  FI: "Finnish",
  FR: "French",
  HU: "Hungarian",
  ID: "Indonesian",
  IT: "Italian",
  JA: "Japanese",
  KO: "Korean",
  LT: "Lithuanian",
  LV: "Latvian",
  NB: "Norwegian (Bokmål)",
  NL: "Dutch",
  PL: "Polish",
  "PT-BR": "Portuguese (Brazilian)",
  "PT-PT": "Portuguese",
  RO: "Romanian",
  RU: "Russian",
  SK: "Slovak",
  SL: "Slovenian",
  SV: "Swedish",
  TR: "Turkish",
  UK: "Ukrainian",
  ZH: "Chinese (simplified)",
};

export const getLanguageNames = (languageCodes?: string[]) => {
  if (!languageCodes) return undefined;
  let names = languageCodes.map((code) => {
    if (languageDictionary[code]) {
      return { code: code, name: languageDictionary[code] };
    } else {
      return { code: code, name: "Unknown" };
    }
  });
  return names;
};

export function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

//Current method can create duplicates, if this becomes problem in the editor, add check to prevent duplicates
function createDynamicStyle(color: string) {
  const style = document.createElement("style");
  style.textContent = `
    .color-${color.slice(1)} {
      background-color: ${hexToRGBA(color, 0.4)};
    }
  `;
  document.head.append(style);
}

export const createTagColorsCSS = (tags: TagModel[]) => {
  tags.forEach((tag) => {
    createDynamicStyle(tag.color);
  });
};

export const getOffsetTopRelativeToEventTranscript = (element: any) => {
  const transcriptEditor = document.querySelector(".scrollable");
  return getAbsoluteOffsetTop(element, transcriptEditor);
};

export const getEventTranscriptOffsetTop = () => {
  const transcriptEditor = document.querySelector(".scrollable");
  return getAbsoluteOffsetTop(transcriptEditor, null);
};
